import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RoomDetail() {
  const { slug } = useParams();
  const [room, setRoom] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/rooms/${slug}`)
      .then((r) => setRoom(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const seo = room ? (
    <Seo
      title={`${room.name} — ${room.tagline}`}
      description={`${room.name} at Hotel Azulo Inn, Safdarjung Enclave, New Delhi. ${room.description?.slice(0, 150) || room.tagline}`}
      path={`/rooms/${slug}`}
      image={room.image}
      imageAlt={`${room.name} at Hotel Azulo Inn`}
    />
  ) : null;

  if (notFound)
    return (
      <div className="pt-40 pb-24 text-center">
        <h1 className="font-serif text-4xl text-ink-900">Room not found</h1>
        <Link to="/rooms" className="mt-6 inline-block text-sage-dark">← Back to rooms</Link>
      </div>
    );
  if (!room) return <div className="pt-40 pb-24 text-center text-ink-500">Loading…</div>;

  return (
    <div className="pt-32 md:pt-40 pb-24">
      {seo}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="overline">{room.view}</div>
          <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.98] text-ink-900">
            {room.name}
          </h1>
          <p className="mt-4 font-serif italic text-sage-dark text-xl md:text-2xl">
            {room.tagline}
          </p>
        </Reveal>
      </div>

      <div className="mt-16 relative h-[60vh] md:h-[78vh] overflow-hidden bg-paper-200">
        <img src={room.image} alt={room.name} className="absolute inset-0 w-full h-full object-cover" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <p className="text-ink-800 text-lg md:text-xl leading-relaxed">
            {room.description}
          </p>
          <div className="mt-10 overline">In every room</div>
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-ink-800 text-sm">
            {room.amenities.map((a) => (
              <li key={a} className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-sage rounded-full" />
                {a}
              </li>
            ))}
          </ul>
        </div>
        <aside className="md:col-span-5 md:pl-10 md:border-l hairline">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-6 text-sm">
            <div>
              <dt className="overline">Size</dt>
              <dd className="mt-2 font-serif text-2xl text-ink-900">{room.size_sqm} m²</dd>
            </div>
            <div>
              <dt className="overline">Occupancy</dt>
              <dd className="mt-2 font-serif text-2xl text-ink-900">up to {room.max_guests}</dd>
            </div>
            <div>
              <dt className="overline">Bed</dt>
              <dd className="mt-2 font-serif text-2xl text-ink-900">{room.bed}</dd>
            </div>
            <div>
              <dt className="overline">Tariff</dt>
              <dd className="mt-2 font-serif italic text-xl text-sage-dark">On enquiry</dd>
            </div>
          </dl>
          <Link
            to={`/book?room=${room.slug}`}
            data-testid={`room-reserve-btn-${room.slug}`}
            className="mt-10 inline-flex items-center justify-center h-12 px-8 bg-sage text-paper-50 text-[12px] tracking-[0.22em] uppercase hover:bg-sage-dark transition-colors"
          >
            Reserve this room
          </Link>
          <p className="mt-4 text-xs text-ink-500 italic">
            Tariffs are dynamic — confirmed by reservations within the day.
          </p>
        </aside>
      </div>

      {room.gallery?.length > 1 && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-24 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {room.gallery.slice(0, 4).map((src, i) => (
            <div key={src} className="relative h-[320px] md:h-[420px] overflow-hidden bg-paper-200">
              <img src={src} alt={`${room.name} at Hotel Azulo Inn — gallery photo ${i + 1}`} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
